import AdminGuard from '@/components/AdminGuard.tsx';
import AuthGuard from '@/components/AuthGuard.tsx';
import ConferenceErrorBoundary from '@/components/ConferenceErrorBoundary.tsx';
import MessageSheet from '@/components/MessageSheet.tsx';
import { PageTitleUpdater } from '@/components/PageTitleUpdater.tsx';
import { Toaster } from '@/components/ui/sonner.tsx';
import { ConfirmationDialogProvider } from '@/hook/useInMeetingConfirmationDialog';
import { useUserSettingsSync } from '@/hook/UserSettingsSync.tsx';
import { setupIntermind } from '@/lib/intermind.ts';
import { authService } from '@/service/AuthService.ts';
import { conferenceService } from '@/service/ConferenceService.ts';
import TagManager from '@sooro-io/react-gtm-module';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider } from 'react-router-dom';
import './index.css';
import HomePage from './pages/HomePage.tsx';
import { useSyncMomentLocale } from '@/hook/useSyncMomentLocale.ts';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

const gtmId = import.meta.env.VITE_GTM_ID;

if (gtmId) {
  TagManager.initialize({
    gtmId,
  });
}

const LoadingFallback = () => {
  const { t } = useTranslation();
  return <MessageSheet message={t('common.loading')} />;
};

interface LazyRouteProps {
  children: React.ReactNode;
  withErrorBoundary?: boolean;
}

const LazyRoute = ({ children, withErrorBoundary = false }: LazyRouteProps) => (
  <Suspense fallback={<LoadingFallback />}>
    {withErrorBoundary ? <ConferenceErrorBoundary>{children}</ConferenceErrorBoundary> : children}
  </Suspense>
);

const NotFoundPage = () => {
  const { t } = useTranslation();
  return <MessageSheet message={t('app.pageNotFound')} />;
};

const lazyLoad = (importFn: () => Promise<{ default: React.ComponentType }>, withErrorBoundary = false) => {
  const Component = lazy(() =>
    importFn().catch((e: Error) => {
      console.error(e);
      return { default: NotFoundPage };
    })
  );
  return (
    <LazyRoute withErrorBoundary={withErrorBoundary}>
      <Component />
    </LazyRoute>
  );
};

const AuthPage = () => lazyLoad(() => import('@/pages/AuthPage.tsx'), true);
const AuthCallbackPage = () => lazyLoad(() => import('@/pages/AuthCallbackPage.tsx'), true);
const HistoryPage = () => lazyLoad(() => import('@/pages/HistoryPage.tsx'), true);
const ConferencePage = () => lazyLoad(() => import('@/pages/ConferencePage.tsx'), true);
const ProfilePage = () => lazyLoad(() => import('@/pages/profile/ProfilePage.tsx'), true);
const ProfileSection = () => lazyLoad(() => import('@/pages/profile/ProfileSection.tsx'), true);
const ProfileBillingSection = () => lazyLoad(() => import('@/pages/profile/ProfileBillingSection.tsx'), true);
const ProfilePromptsSection = () => lazyLoad(() => import('@/pages/profile/ProfilePromptsSection.tsx'), true);
const SettingsSection = () => lazyLoad(() => import('@/pages/profile/SettingsSection.tsx'), true);
const AdvancedSection = () => lazyLoad(() => import('@/pages/profile/AdvancedSection.tsx'), true);
const PricingPage = () => lazyLoad(() => import('@/pages/PricingPage.tsx'));
const CheckoutPage = () => lazyLoad(() => import('@/pages/checkout/CheckoutPage.tsx'));
const CheckoutSuccessPage = () => lazyLoad(() => import('@/pages/checkout/CheckoutSuccessPage.tsx'));
const CheckoutCancelPage = () => lazyLoad(() => import('@/pages/checkout/CheckoutCancelPage.tsx'));
const AdminPage = () => lazyLoad(() => import('@/pages/admin/AdminPage.tsx'), true);
const DashboardSection = () => lazyLoad(() => import('@/pages/admin/DashboardSection.tsx'), true);
const UsersSection = () => lazyLoad(() => import('@/pages/admin/UsersSection.tsx'), true);
const AdminBillingSection = () => lazyLoad(() => import('@/pages/admin/AdminBillingSection.tsx'), true);
const AdminPromptsSection = () => lazyLoad(() => import('@/pages/admin/AdminPromptsSection.tsx'), true);
const GoogleCalendarCallbackPage = () => lazyLoad(() => import('@/pages/GoogleCalendarCallbackPage.tsx'));
const AskAnythingPage = () => lazyLoad(() => import('@/pages/ask/AskAnythingPage.tsx'));

const RootLayout = () => {
  return (
    <>
      <PageTitleUpdater />
      <Outlet />
    </>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<RootLayout />}>
      <Route index element={<HomePage />} />

      {/* Auth routes */}
      <Route path="auth" element={<AuthPage />} />
      <Route path="auth/callback" element={<AuthCallbackPage />} />

      {/* Public routes */}
      <Route path=":roomId" element={<ConferencePage />} />
      <Route path="pricing" element={<PricingPage />} />
      <Route path="ask" element={<AskAnythingPage />} />

      {/* Protected routes */}
      <Route element={<AuthGuard />}>
        <Route path="history/:recordId?" element={<HistoryPage />} />
        <Route path="profile" element={<ProfilePage />}>
          <Route index element={<ProfileSection />} />
          <Route path="billing" element={<ProfileBillingSection />} />
          <Route path="settings" element={<SettingsSection />} />
          <Route path="prompts" element={<ProfilePromptsSection />} />
          <Route path="advanced" element={<AdvancedSection />} />
        </Route>

        <Route path="google/calendar/auth/callback" element={<GoogleCalendarCallbackPage />} />

        <Route path="checkout" element={<CheckoutPage />} />
        <Route path="checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="checkout/cancel" element={<CheckoutCancelPage />} />

        <Route element={<AdminGuard />}>
          <Route path="admin" element={<AdminPage />}>
            <Route index element={<DashboardSection />} />
            <Route path="users/:userId?" element={<UsersSection />} />
            <Route path="billing" element={<AdminBillingSection />} />
            <Route path="prompts" element={<AdminPromptsSection />} />
          </Route>
        </Route>
      </Route>
    </Route>
  )
);

function App() {
  // Load user settings from the server
  useUserSettingsSync();
  useSyncMomentLocale();

  const { t } = useTranslation();
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const [initialized, setInitialized] = useState(false);

  const scheduleTokenRefresh = useCallback((): void => {
    const expIn: number = authService.getAccessTokenExpiresIn();
    if (expIn > 0) {
      const timeout = expIn > 5000 ? expIn - 5000 : 0; // 5 seconds before expiry
      timeoutIdRef.current = setTimeout(async () => {
        await authService?.refreshAccessToken();
        scheduleTokenRefresh();
      }, timeout);
    }
  }, []);

  // Initialize intermind and setup token refresh
  useEffect(() => {
    (async () => {
      await setupIntermind();
      scheduleTokenRefresh();
      setInitialized(true);

      return () => {
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }
      };
    })();
  }, [scheduleTokenRefresh]);

  // Reload the page when the user returns from another tab
  useEffect(() => {
    window.onpageshow = function (event) {
      if (event.persisted) {
        window.location.reload();
      }
    };

    const handleDOMContentLoaded = async () => {
      await authService?.getUser();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', handleDOMContentLoaded);
    } else {
      handleDOMContentLoaded().catch(console.error);
    }

    return () => {
      document.removeEventListener('DOMContentLoaded', handleDOMContentLoaded);
    };
  }, []);

  // Clean up on conference leave
  useEffect(() => {
    const handleBeforeUnload = () => {
      conferenceService.closeSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        setupIntermind().catch((e) => console.error('Error setting up intermind', e));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  if (!initialized) {
    return <MessageSheet message={t('app.initializing')} />;
  }

  return (
    <>
      <ConfirmationDialogProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ConfirmationDialogProvider>
      <Toaster
        position="top-right"
        theme="light"
        toastOptions={{
          closeButton: true,
          classNames: {
            actionButton: '!bg-indigo-600 !text-white',
            closeButton: 'bg-white absolute top-1 right-1 left-auto transform-none border-none',
          },
        }}
      />
      {/* <PWAInstallPrompt /> */}
    </>
  );
}

export default App;
